import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createWidget,
  DEFAULT_WIDGET_CONFIG,
  getWidget,
  HEX_COLOR_REGEX,
  updateWidget,
  type Widget,
  type WidgetBodyPattern,
  type WidgetConfig,
  type WidgetCreate,
  type WidgetDesign,
  type WidgetLauncherPosition,
  type WidgetPatch,
} from '../api/widgets.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const DESIGNS: WidgetDesign[] = ['rounded', 'square', 'minimal'];
const BODY_PATTERNS: WidgetBodyPattern[] = ['dots', 'lines', 'solid', 'none'];
const LAUNCHERS: WidgetLauncherPosition[] = ['left', 'right'];
const FONTS = ['system-ui', 'serif', 'monospace', 'Inter, sans-serif', 'Arial, sans-serif'];

const COLOR_FIELDS: ReadonlyArray<{
  key: keyof Pick<
    WidgetConfig,
    | 'primaryColor'
    | 'headerColor'
    | 'backgroundColor'
    | 'userBubbleColor'
    | 'agentBubbleColor'
    | 'textColor'
    | 'mutedColor'
  >;
  label: string;
}> = [
  { key: 'primaryColor', label: 'primaryColor' },
  { key: 'headerColor', label: 'headerColor' },
  { key: 'backgroundColor', label: 'backgroundColor' },
  { key: 'userBubbleColor', label: 'userBubbleColor' },
  { key: 'agentBubbleColor', label: 'agentBubbleColor' },
  { key: 'textColor', label: 'textColor' },
  { key: 'mutedColor', label: 'mutedColor' },
];

function bodyPatternStyle(pattern: WidgetBodyPattern, base: string): React.CSSProperties {
  switch (pattern) {
    case 'dots':
      return {
        background: `radial-gradient(circle, #94a3b8 1px, transparent 1px) 0 0 / 12px 12px, ${base}`,
      };
    case 'lines':
      return {
        background: `repeating-linear-gradient(45deg, #e2e8f0 0 1px, transparent 1px 10px), ${base}`,
      };
    case 'solid':
      return { background: base };
    case 'none':
    default:
      return { background: base };
  }
}

export interface WidgetFormProps {
  mode: 'create' | 'edit';
}

export function WidgetForm({ mode }: WidgetFormProps): JSX.Element {
  const { id = '', widgetId = '' } = useParams<{ id: string; widgetId?: string }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const widgetQuery = useQuery({
    queryKey: ['widget', id, widgetId],
    queryFn: () => getWidget(id, widgetId),
    enabled: mode === 'edit' && Boolean(id && widgetId),
  });

  const initial = useMemo<{ name: string; isDefault: boolean; config: WidgetConfig }>(() => {
    if (mode === 'edit' && widgetQuery.data) {
      return {
        name: widgetQuery.data.name,
        isDefault: widgetQuery.data.isDefault,
        config: { ...widgetQuery.data.config },
      };
    }
    return { name: '', isDefault: false, config: { ...DEFAULT_WIDGET_CONFIG } };
  }, [mode, widgetQuery.data]);

  const [name, setName] = useState(initial.name);
  const [isDefault, setIsDefault] = useState(initial.isDefault);
  const [config, setConfig] = useState<WidgetConfig>(initial.config);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'closed' | 'open'>('open');

  useEffect(() => {
    setName(initial.name);
    setIsDefault(initial.isDefault);
    setConfig(initial.config);
  }, [initial]);

  function updateConfig<K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]): void {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }

  const createMutation = useMutation({
    mutationFn: (body: WidgetCreate) => createWidget(id, body),
    onSuccess: () => {
      pushToast('success', t.widget.created);
      queryClient.invalidateQueries({ queryKey: ['widgets', id] });
      navigate(`/tenants/${id}/widgets`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: WidgetPatch) => {
      const current = widgetQuery.data;
      if (!current) throw new Error('widget_not_loaded');
      return updateWidget(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.widget.updated);
      queryClient.invalidateQueries({ queryKey: ['widgets', id] });
      queryClient.invalidateQueries({ queryKey: ['widget', id, widgetId] });
      navigate(`/tenants/${id}/widgets`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.widget.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  function validate(): string | null {
    const trimmed = name.trim();
    if (!trimmed) return t.widget.nameRequired;
    if (trimmed.length > 120) return t.widget.nameTooLong;
    for (const f of COLOR_FIELDS) {
      if (!HEX_COLOR_REGEX.test(config[f.key])) return `${f.label}: ${t.widget.colorInvalid}`;
    }
    if (!config.fontFamily.trim()) return t.widget.fontFamilyRequired;
    if (config.title.length > 40) return t.widget.titleTooLong;
    if (config.subtitle.length > 80) return t.widget.subtitleTooLong;
    return null;
  }

  function onSubmit(ev: React.FormEvent): void {
    ev.preventDefault();
    const err = validate();
    setErrMsg(err);
    if (err) {
      pushToast('error', err);
      return;
    }
    const trimmedName = name.trim();
    if (mode === 'create') {
      createMutation.mutate({ name: trimmedName, isDefault, config });
    } else if (widgetQuery.data) {
      const current = widgetQuery.data;
      const patch: WidgetPatch = {};
      if (trimmedName !== current.name) patch.name = trimmedName;
      if (isDefault !== current.isDefault) patch.isDefault = isDefault;
      if (JSON.stringify(config) !== JSON.stringify(current.config)) patch.config = config;
      if (Object.keys(patch).length === 0) {
        pushToast('success', t.widget.updated);
        navigate(`/tenants/${id}/widgets`);
        return;
      }
      updateMutation.mutate(patch);
    }
  }

  if (mode === 'edit' && widgetQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && widgetQuery.error) {
    return <p className="text-red-700">{(widgetQuery.error as Error).message}</p>;
  }

  const submitting = createMutation.isPending || updateMutation.isPending;
  const radiusByDesign =
    config.design === 'square' ? 0 : config.design === 'minimal' ? 4 : config.borderRadius;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.widget.newTitle : t.widget.editTitle}
      </h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded border border-slate-200 bg-white p-5"
          noValidate
        >
          <Field label={t.widget.fields.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              required
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            {t.widget.fields.isDefault}
          </label>

          <Section title={t.widget.sectionColors}>
            <div className="grid grid-cols-2 gap-3">
              {COLOR_FIELDS.map((f) => (
                <Field key={f.key} label={t.widget.fields[f.key]}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config[f.key]}
                      onChange={(e) => updateConfig(f.key, e.target.value)}
                      aria-label={f.label}
                      className="h-8 w-12 cursor-pointer rounded border border-slate-300"
                    />
                    <input
                      type="text"
                      value={config[f.key]}
                      onChange={(e) => updateConfig(f.key, e.target.value)}
                      pattern="^#[0-9a-fA-F]{6}$"
                      className="w-28 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                    />
                  </div>
                </Field>
              ))}
            </div>
          </Section>

          <Section title={t.widget.sectionStyle}>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t.widget.fields.design}>
                <select
                  value={config.design}
                  onChange={(e) => updateConfig('design', e.target.value as WidgetDesign)}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                >
                  {DESIGNS.map((d) => (
                    <option key={d} value={d}>
                      {t.widget.design[d]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t.widget.fields.fontFamily}>
                <select
                  value={config.fontFamily}
                  onChange={(e) => updateConfig('fontFamily', e.target.value)}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                >
                  {FONTS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={`${t.widget.fields.borderRadius}: ${config.borderRadius}px`}>
                <input
                  type="range"
                  min={0}
                  max={32}
                  value={config.borderRadius}
                  onChange={(e) => updateConfig('borderRadius', Number(e.target.value))}
                  className="w-full"
                />
              </Field>
              <Field label={t.widget.fields.launcherPosition}>
                <div className="flex gap-2">
                  {LAUNCHERS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => updateConfig('launcherPosition', l)}
                      className={`rounded border px-3 py-1 text-xs ${
                        config.launcherPosition === l
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-slate-300'
                      }`}
                    >
                      {t.widget.launcher[l]}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={t.widget.fields.bodyPattern}>
                <div className="flex flex-wrap gap-2">
                  {BODY_PATTERNS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => updateConfig('bodyPattern', b)}
                      className={`rounded border px-3 py-1 text-xs ${
                        config.bodyPattern === b
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-slate-300'
                      }`}
                    >
                      {t.widget.bodyPattern[b]}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          </Section>

          <Section title={t.widget.sectionTexts}>
            <Field label={`${t.widget.fields.title} (${config.title.length}/40)`}>
              <input
                type="text"
                value={config.title}
                maxLength={40}
                onChange={(e) => updateConfig('title', e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field label={`${t.widget.fields.subtitle} (${config.subtitle.length}/80)`}>
              <input
                type="text"
                value={config.subtitle}
                maxLength={80}
                onChange={(e) => updateConfig('subtitle', e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
          </Section>

          <Section title={t.widget.sectionImages}>
            <Field label={t.widget.fields.profileImageUrl}>
              <input
                type="text"
                value={config.profileImageUrl ?? ''}
                onChange={(e) =>
                  updateConfig('profileImageUrl', e.target.value || undefined)
                }
                placeholder="https://… o data:image/…"
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </Field>
            <Field label={t.widget.fields.launcherIconUrl}>
              <input
                type="text"
                value={config.launcherIconUrl ?? ''}
                onChange={(e) =>
                  updateConfig('launcherIconUrl', e.target.value || undefined)
                }
                placeholder="https://… o data:image/…"
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </Field>
          </Section>

          {errMsg && (
            <p role="alert" className="text-xs text-red-700">
              {errMsg}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Link
              to={`/tenants/${id}/widgets`}
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
            >
              {t.app.cancel}
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting ? '…' : t.app.save}
            </button>
          </div>
        </form>

        <div className="sticky top-4 self-start">
          <div className="mb-2 flex gap-2 text-xs">
            <button
              type="button"
              onClick={() => setPreviewMode('closed')}
              className={`rounded border px-3 py-1 ${
                previewMode === 'closed'
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-300'
              }`}
            >
              {t.widget.previewClosed}
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('open')}
              className={`rounded border px-3 py-1 ${
                previewMode === 'open'
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-300'
              }`}
            >
              {t.widget.previewOpen}
            </button>
          </div>
          <WidgetMockPreview config={config} mode={previewMode} radius={radiusByDesign} />
        </div>
      </div>
    </div>
  );
}

interface WidgetMockPreviewProps {
  config: WidgetConfig;
  mode: 'closed' | 'open';
  radius: number;
}

function WidgetMockPreview({ config, mode, radius }: WidgetMockPreviewProps): JSX.Element {
  const containerStyle: React.CSSProperties = {
    width: 360,
    height: 560,
    fontFamily: config.fontFamily,
    color: config.textColor,
    border: '1px solid #cbd5e1',
    borderRadius: radius,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    ...bodyPatternStyle(config.bodyPattern, config.backgroundColor),
  };

  if (mode === 'closed') {
    return (
      <div
        data-testid="widget-preview-closed"
        style={{
          width: 360,
          height: 560,
          position: 'relative',
          background: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderRadius: 12,
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            [config.launcherPosition === 'left' ? 'left' : 'right']: 16,
            width: 56,
            height: 56,
            borderRadius: 28,
            background: config.primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {config.launcherIconUrl ? (
            <img
              src={config.launcherIconUrl}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 16 }}
            />
          ) : (
            '💬'
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="widget-preview-open" style={containerStyle}>
      <div
        style={{
          background: config.headerColor,
          color: '#fff',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {config.profileImageUrl && (
          <img
            src={config.profileImageUrl}
            alt=""
            style={{ width: 32, height: 32, borderRadius: 16, background: '#fff' }}
          />
        )}
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{config.title || 'Hola'}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            {config.subtitle || '¿En qué te ayudamos?'}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            alignSelf: 'flex-start',
            background: config.agentBubbleColor,
            color: config.textColor,
            padding: '8px 12px',
            borderRadius: radius,
            maxWidth: '80%',
            fontSize: 13,
          }}
        >
          Hola 👋 ¿En qué puedo ayudarte?
        </div>
        <div
          style={{
            alignSelf: 'flex-end',
            background: config.userBubbleColor,
            color: '#fff',
            padding: '8px 12px',
            borderRadius: radius,
            maxWidth: '80%',
            fontSize: 13,
          }}
        >
          Necesito info de horarios.
        </div>
        <div style={{ color: config.mutedColor, fontSize: 11, marginTop: 'auto' }}>
          Powered by ZEngine
        </div>
      </div>
      <div
        style={{
          borderTop: '1px solid rgba(0,0,0,0.08)',
          padding: 8,
          display: 'flex',
          gap: 6,
          background: '#fff',
        }}
      >
        <input
          placeholder="Escribí…"
          readOnly
          style={{
            flex: 1,
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            padding: '6px 8px',
            fontSize: 13,
          }}
        />
        <button
          type="button"
          style={{
            background: config.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: 13,
          }}
        >
          →
        </button>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3 rounded border border-slate-100 bg-slate-50/40 p-3">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

// Marker: exported `Widget` model not used here; keep import path consistent.
export type { Widget };
